import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';

/* ------------------------------------------------------------------ */
/*  Tipos                                                              */
/* ------------------------------------------------------------------ */

export interface TabItem {
  id: string;
  label: string;
  icon?: React.ReactNode;
  badge?: {
    content: string | number;
    variant?: 'neutral' | 'primary' | 'success' | 'warning';
  };
  disabled?: boolean;
}

/** Variantes visuales del TabBar. `underline` es la base. */
export type TabBarVariant =
  | 'underline'
  | 'with-background'
  | 'pill'
  | 'segmented'
  | 'vertical';

export type TabBarSize = 'sm' | 'md' | 'lg';

/**
 * Estrategia cuando los tabs no caben (solo variantes horizontales):
 * - `scroll`: se deslizan, con bordes difuminados + flechas.
 * - `menu`:   los que no caben se agrupan en un desplegable "Más".
 */
export type TabBarOverflow = 'scroll' | 'menu';

export interface TabBarProps {
  items: TabItem[];
  activeTab: string;
  onChange: (tabId: string) => void;
  variant?: TabBarVariant;
  /**
   * Si se omite, se adapta a la pantalla: `sm` por debajo de 1536 px y `md`
   * desde 1536 px. Si se indica, se respeta en cualquier pantalla.
   */
  size?: TabBarSize;
  /** @default 'scroll'. Se ignora en la variante `vertical`. */
  overflow?: TabBarOverflow;
  /** Los tabs ocupan todo el ancho disponible. @default false */
  fullWidth?: boolean;
  className?: string;
  /** Etiqueta del botón de desbordamiento. @default 'Más' */
  moreLabel?: string;
}

/* ------------------------------------------------------------------ */
/*  Mapas de tamaño                                                    */
/* ------------------------------------------------------------------ */

const SIZE: Record<
  TabBarSize | 'auto',
  { font: string; padX: string; padY: string; gap: string; box: string; icon: string }
> = {
  // auto → sm por debajo de 1536 px (laptops) y md desde 1536 px.
  // Es lo que se usa cuando no se indica `size`.
  auto: {
    font: 'text-[13px] leading-5 2xl:text-sm',
    padX: 'px-1',
    padY: 'py-[6px] 2xl:py-2',
    gap: 'gap-4 2xl:gap-6',
    box: 'w-4 h-4 2xl:w-5 2xl:h-5',
    icon: '[&>svg]:w-4 [&>svg]:h-4 2xl:[&>svg]:w-[18px] 2xl:[&>svg]:h-[18px]',
  },
  // sm → texto 13px · icono 16px / cajita 16px
  sm: {
    font: 'text-[13px] leading-5',
    padX: 'px-1',
    padY: 'py-[6px]',
    gap: 'gap-4',
    box: 'w-4 h-4',
    icon: '[&>svg]:w-4 [&>svg]:h-4',
  },
  // md → texto 14px · icono 18px / cajita 20px
  md: {
    font: 'text-sm',
    padX: 'px-1',
    padY: 'py-2',
    gap: 'gap-6',
    box: 'w-5 h-5',
    icon: '[&>svg]:w-[18px] [&>svg]:h-[18px]',
  },
  // lg → texto 16px · icono 20px / cajita 24px
  lg: {
    font: 'text-base',
    padX: 'px-1.5',
    padY: 'py-[10px]',
    gap: 'gap-8',
    box: 'w-6 h-6',
    icon: '[&>svg]:w-5 [&>svg]:h-5',
  },
};

/* ------------------------------------------------------------------ */
/*  Componente                                                         */
/* ------------------------------------------------------------------ */

export const TabBar: React.FC<TabBarProps> = ({
  items,
  activeTab,
  onChange,
  variant = 'underline',
  size,
  overflow = 'scroll',
  fullWidth = false,
  className = '',
  moreLabel = 'Más',
}) => {
  const isVertical = variant === 'vertical';
  const useMenu = overflow === 'menu' && !isVertical;
  // Sin `size` explícito, el tamaño se adapta a la pantalla (ver SIZE.auto)
  const sizeKey = size ?? 'auto';

  /* ---- refs y estado de overflow -------------------------------- */
  const listRef = useRef<HTMLDivElement>(null);
  const ghostRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const [visibleCount, setVisibleCount] = useState(items.length);
  const [menuOpen, setMenuOpen] = useState(false);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  /* ---- medición para overflow=menu ------------------------------ */
  const recompute = useCallback(() => {
    if (!useMenu || !listRef.current || !ghostRef.current) return;
    const container = listRef.current.clientWidth;
    const children = Array.from(ghostRef.current.children) as HTMLElement[];
    const RESERVED = 78; // ancho aprox. del botón "Más"
    const GAP = 24;

    let used = 0;
    let count = 0;
    for (let i = 0; i < children.length; i++) {
      used += children[i].offsetWidth + (i > 0 ? GAP : 0);
      if (used <= container) count++;
      else break;
    }
    // Si no caben todos, reservamos espacio para "Más"
    if (count < items.length) {
      used = 0;
      count = 0;
      for (let i = 0; i < children.length; i++) {
        used += children[i].offsetWidth + (i > 0 ? GAP : 0);
        if (used <= container - RESERVED) count++;
        else break;
      }
    }
    setVisibleCount(Math.max(1, count));
  }, [useMenu, items.length]);

  /* ---- actualización de fades/flechas para overflow=scroll ------ */
  const updateScroll = useCallback(() => {
    const el = listRef.current;
    if (!el || useMenu || isVertical) return;
    const max = el.scrollWidth - el.clientWidth - 1;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft < max);
  }, [useMenu, isVertical]);

  useLayoutEffect(() => {
    recompute();
    updateScroll();
  }, [recompute, updateScroll, items, sizeKey, variant]);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      recompute();
      updateScroll();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [recompute, updateScroll]);

  /* ---- cerrar menú al hacer click fuera ------------------------- */
  useEffect(() => {
    if (!menuOpen) return;
    const close = () => setMenuOpen(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [menuOpen]);

  /* ---- al activar un tab, asegúralo visible (scroll) ------------ */
  useEffect(() => {
    if (useMenu || isVertical) return;
    const el = tabRefs.current[activeTab];
    el?.scrollIntoView({ inline: 'nearest', block: 'nearest' });
  }, [activeTab, useMenu, isVertical]);

  /* ---- navegación por teclado ----------------------------------- */
  const focusables = items.filter((i) => !i.disabled);

  const handleKeyDown = (e: React.KeyboardEvent, id: string) => {
    const nextKey = isVertical ? 'ArrowDown' : 'ArrowRight';
    const prevKey = isVertical ? 'ArrowUp' : 'ArrowLeft';
    const idx = focusables.findIndex((i) => i.id === id);
    if (idx === -1) return;

    let target: TabItem | undefined;
    if (e.key === nextKey) target = focusables[(idx + 1) % focusables.length];
    else if (e.key === prevKey)
      target = focusables[(idx - 1 + focusables.length) % focusables.length];
    else if (e.key === 'Home') target = focusables[0];
    else if (e.key === 'End') target = focusables[focusables.length - 1];
    else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onChange(id);
      return;
    } else return;

    e.preventDefault();
    if (target) {
      onChange(target.id);
      tabRefs.current[target.id]?.focus();
    }
  };

  /* ---- badge ----------------------------------------------------- */
  const renderBadge = (badge?: TabItem['badge']) => {
    if (!badge) return null;
    const variants = {
      neutral: 'bg-[var(--surface-2)] text-[var(--text-secondary)]',
      primary: 'bg-[var(--primary-light)] text-[var(--primary)]',
      success:
        'bg-[var(--success-bg)] text-[var(--success)] border border-[var(--success-border)]',
      warning:
        'bg-[var(--warning-bg)] text-[var(--warning)] border border-[var(--warning-border)] animate-pulse-subtle',
    };
    return (
      <span
        className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-xs font-semibold ${
          variants[badge.variant || 'neutral']
        }`}
      >
        {badge.content}
      </span>
    );
  };

  /* ---- clases por tab ------------------------------------------- */
  const tabClasses = (isActive: boolean, disabled?: boolean) => {
    const s = SIZE[sizeKey];
    const base = `flex items-center gap-2 font-medium cursor-pointer
      transition-[color,background-color,border-color] duration-150
      focus-visible:outline-2 focus-visible:outline-[var(--primary)] focus-visible:outline-offset-4 focus-visible:rounded ${s.font}`;

    const off = disabled ? 'opacity-50 cursor-not-allowed' : '';

    switch (variant) {
      case 'with-background':
        return `${base} whitespace-nowrap ${s.padY} px-4 rounded-t-lg border-b-2 ${off} ${
          isActive
            ? 'border-[var(--primary)] text-[var(--primary)] bg-[var(--surface-hover)]'
            : 'border-transparent text-[var(--text-tertiary)] hover:text-[var(--primary)] hover:bg-[var(--surface-hover)]'
        }`;

      case 'pill':
        return `${base} whitespace-nowrap px-4 py-2 rounded-lg ${off} ${
          isActive
            ? 'bg-[var(--primary-light)] text-[var(--primary)]'
            : 'text-[var(--text-tertiary)] hover:text-[var(--primary)] hover:bg-[var(--surface-hover)]'
        }`;

      case 'segmented':
        return `${base} whitespace-nowrap px-4 py-1.5 rounded-md ${off} ${
          isActive
            ? 'bg-[var(--surface-0)] text-[var(--text-primary)] shadow-[var(--shadow-segment)]'
            : 'text-[var(--text-tertiary)] hover:text-[var(--primary)]'
        }`;

      case 'vertical':
        return `${base} w-full justify-start text-left px-3 py-2 rounded-lg ${off} ${
          isActive
            ? 'bg-[var(--primary-light)] text-[var(--primary)]'
            : 'text-[var(--text-primary)] hover:text-[var(--primary)] hover:bg-[var(--surface-hover)]'
        }`;

      // underline (base)
      default:
        return `${base} whitespace-nowrap ${s.padX} ${s.padY} border-b-2 ${off} ${
          isActive
            ? 'border-[var(--primary)] text-[var(--primary)]'
            : 'border-transparent text-[var(--text-tertiary)] hover:text-[var(--primary)] hover:border-[var(--border-default)]'
        }`;
    }
  };

  /* ---- render de un botón --------------------------------------- */
  const renderTab = (item: TabItem) => {
    const isActive = item.id === activeTab;
    return (
      <button type="button"
        key={item.id}
        ref={(el) => {
          tabRefs.current[item.id] = el;
        }}
        role="tab"
        aria-selected={isActive}
        aria-controls={`tabpanel-${item.id}`}
        tabIndex={isActive ? 0 : -1}
        disabled={item.disabled}
        onClick={() => !item.disabled && onChange(item.id)}
        onKeyDown={(e) => !item.disabled && handleKeyDown(e, item.id)}
        className={`${tabClasses(isActive, item.disabled)} ${
          fullWidth && !isVertical ? 'flex-1 justify-center' : ''
        }`}
      >
        {item.icon && (
          <span
            className={`flex items-center justify-center shrink-0 ${SIZE[sizeKey].box} ${SIZE[sizeKey].icon}`}
          >
            {item.icon}
          </span>
        )}
        <span>{item.label}</span>
        {renderBadge(item.badge)}
      </button>
    );
  };

  /* ================================================================ */
  /*  VARIANTE VERTICAL                                               */
  /* ================================================================ */
  if (isVertical) {
    return (
      <div
        role="tablist"
        aria-orientation="vertical"
        className={`flex flex-col gap-1 ${className}`}
      >
        {items.map(renderTab)}
      </div>
    );
  }

  /* ================================================================ */
  /*  CONTENEDORES (pill / segmented usan riel; el resto borde)      */
  /* ================================================================ */
  const isRail = variant === 'pill' || variant === 'segmented';
  const railBg =
    variant === 'segmented' ? 'bg-[var(--surface-2)] p-1' : 'bg-[var(--surface-1)] p-2';

  const outerClasses = isRail
    ? `inline-flex rounded-lg ${railBg} ${className}`
    : `relative border-b border-[var(--border-default)] ${className}`;

  /* ---- partición visible / oculto para overflow=menu ------------ */
  const visible = useMenu ? items.slice(0, visibleCount) : items;
  const hidden = useMenu ? items.slice(visibleCount) : [];
  const activeHidden = hidden.some((i) => i.id === activeTab);

  return (
    <div className={outerClasses}>
      {/* fila de medición invisible (solo para overflow=menu) */}
      {useMenu && (
        <div
          ref={ghostRef}
          aria-hidden="true"
          className="absolute -z-10 opacity-0 pointer-events-none flex gap-6"
        >
          {items.map((item) => (
            <button type="button" key={item.id} className={tabClasses(false)}>
              {item.icon && <span className={`shrink-0 ${SIZE[sizeKey].box}`} />}
              <span>{item.label}</span>
              {renderBadge(item.badge)}
            </button>
          ))}
        </div>
      )}

      {/* lista real */}
      <div
        ref={listRef}
        role="tablist"
        onScroll={updateScroll}
        className={`flex ${SIZE[sizeKey].gap} ${
          isRail ? 'gap-2' : ''
        } ${!isRail ? '-mb-px' : ''} ${
          useMenu
            ? 'flex-nowrap items-end'
            : 'overflow-x-auto items-end [&::-webkit-scrollbar]:hidden'
        } ${fullWidth ? 'w-full' : ''}`}
        style={!useMenu ? { scrollbarWidth: 'none' } : undefined}
      >
        {visible.map(renderTab)}

        {/* botón "Más" */}
        {useMenu && hidden.length > 0 && (
          <div className="relative">
            <button
              type="button"
              aria-haspopup="true"
              aria-expanded={menuOpen}
              onClick={(e) => {
                e.stopPropagation();
                setMenuOpen((v) => !v);
              }}
              className={`${tabClasses(activeHidden)} `}
            >
              <span>{moreLabel}</span>
              <ChevronDown className="w-4 h-4" />
            </button>

            {menuOpen && (
              <div
                role="menu"
                className="absolute right-0 top-full mt-1.5 min-w-[180px] z-20
                  bg-[var(--surface-0)] border border-[var(--border-default)]
                  rounded-lg shadow-lg p-1.5"
                onClick={(e) => e.stopPropagation()}
              >
                {hidden.map((item) => {
                  const isActive = item.id === activeTab;
                  return (
                    <button type="button"
                      key={item.id}
                      role="menuitem"
                      disabled={item.disabled}
                      onClick={() => {
                        if (item.disabled) return;
                        onChange(item.id);
                        setMenuOpen(false);
                      }}
                      className={`flex w-full items-center gap-2 px-2.5 py-2 rounded-md
                        text-sm font-medium text-left transition-colors
                        ${
                          isActive
                            ? 'bg-[var(--primary-light)] text-[var(--primary)]'
                            : 'text-[var(--text-primary)] hover:bg-[var(--surface-hover)]'
                        } ${item.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {item.icon && (
                        <span className="w-5 h-5 flex items-center justify-center">
                          {item.icon}
                        </span>
                      )}
                      <span className="flex-1">{item.label}</span>
                      {renderBadge(item.badge)}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* bordes difuminados + flechas (solo overflow=scroll) */}
      {!useMenu && (
        <>
          <div
            aria-hidden
            className={`pointer-events-none absolute left-0 top-0 bottom-px w-8
              bg-gradient-to-r from-[var(--surface-0)] to-transparent
              transition-opacity duration-200 ${canLeft ? 'opacity-100' : 'opacity-0'}`}
          />
          <div
            aria-hidden
            className={`pointer-events-none absolute right-0 top-0 bottom-px w-8
              bg-gradient-to-l from-[var(--surface-0)] to-transparent
              transition-opacity duration-200 ${canRight ? 'opacity-100' : 'opacity-0'}`}
          />
          {canLeft && (
            <button
              type="button"
              aria-label="Anterior"
              onClick={() => listRef.current?.scrollBy({ left: -160, behavior: 'smooth' })}
              className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-7 h-7 rounded-full
                flex items-center justify-center bg-[var(--surface-0)]
                border border-[var(--border-default)] text-[var(--text-primary)]
                hover:bg-[var(--surface-hover)]"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
          )}
          {canRight && (
            <button
              type="button"
              aria-label="Siguiente"
              onClick={() => listRef.current?.scrollBy({ left: 160, behavior: 'smooth' })}
              className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-7 h-7 rounded-full
                flex items-center justify-center bg-[var(--surface-0)]
                border border-[var(--border-default)] text-[var(--text-primary)]
                hover:bg-[var(--surface-hover)]"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </>
      )}
    </div>
  );
};

TabBar.displayName = 'TabBar';

export default TabBar;



